/**
 * ベタ書き URL の検出と正規化。
 *
 * 表示側の自動リンク化（autoLink.js）と Quill エディタ側の入力 / 貼り付け時の
 * リンク化が同じ判定を共有するため、URL の定義はこのファイルを唯一の情報源とする。
 * 対象は http / https と www. 始まりのみ。スキームもドット区切りも無い
 * 「example」のような文字列は、本文中の誤検出が多いため対象外とする。
 */

// RFC 3986 で URL に使える ASCII 文字のみを本体として扱う。全角文字を含めないことで
// 「https://example.com をご覧ください」の日本語部分を URL に巻き込まない。
const URL_BODY_CHARS = "A-Za-z0-9\\-._~:/?#\\[\\]@!$&'()*+,;=%";

const URL_PATTERN = new RegExp(`(?:https?://|www\\.)[${URL_BODY_CHARS}]+`, "gi");

// 検出位置の直前がこれらの文字なら、より大きなトークンの一部と見なして採用しない。
// メールアドレス（foo@www.example.com）や「xwww.example.com」の誤検出を防ぐ。
const PRECEDING_REJECT = /[A-Za-z0-9@._%+-]/;

// 本文の句読点が URL 末尾に食い込んだ分を取り除く。全角の句読点は URL_BODY_CHARS に
// 含まれないため、ここで扱うのは半角のみでよい。
const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/;

const CLOSING_BRACKETS = { ")": "(", "]": "[", "}": "{" };

const MAX_URL_LENGTH = 2048;

function countChar(text, char) {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === char) count++;
  }
  return count;
}

function trimTrailingPunctuation(url) {
  let trimmed = url;
  let previous = null;
  while (trimmed !== previous) {
    previous = trimmed;
    trimmed = trimmed.replace(TRAILING_PUNCTUATION, "");
    const last = trimmed.slice(-1);
    const opening = CLOSING_BRACKETS[last];
    // 「/wiki/Foo_(bar)」のように対応が取れている閉じ括弧は URL の一部として残し、
    // 「（https://example.com）」のように対応が取れていない分だけ取り除く。
    if (opening && countChar(trimmed, last) > countChar(trimmed, opening)) {
      trimmed = trimmed.slice(0, -1);
    }
  }
  return trimmed;
}

// 区切り文字の直後に新しいスキームが始まる場合、そこから先は別の URL とみなす。
// 「https://a.example.com,https://b.example.com」のように URL を並べて書いたときに、
// 全体が1本の壊れたリンクになるのを防ぐ。
//
// 対象を , ; : に絞っているのは、「その文字の直後にスキームが現れることが URL として
// ありえないか」で選んでいるため。RFC 3986 で使える文字を一律に対象とすると、
// .../r?u=https://... のようなリダイレクト URL が = の位置で分割されて壊れる。
//   : … ホスト名の後のコロンはポート番号で数字しか取れないため、:https:// は構造的に
//       ありえない。:8080 は先読みがスキームに一致しないので影響を受けない。
//   , ; … クエリ値に生の URL を入れれば理論上はありうるが、通常はパーセントエンコード
//          されるため実質ありえない。なお URL 内の正当なカンマ（.../map@35.6,139.7）は
//          直後が数字なので切られない。
const SEPARATOR_BEFORE_SCHEME = /[,;:](?=(?:https?:\/\/|www\.))/i;

// 1件の検出結果を、区切り位置で複数の断片に割る。offset は元の文字列上の相対位置。
function splitAtEmbeddedScheme(url) {
  const segments = [];
  let offset = 0;
  let rest = url;

  let separator;
  while ((separator = rest.search(SEPARATOR_BEFORE_SCHEME)) !== -1) {
    segments.push({ offset, value: rest.slice(0, separator) });
    // 区切り文字そのものはどちらの URL にも含めない。
    offset += separator + 1;
    rest = rest.slice(separator + 1);
  }
  segments.push({ offset, value: rest });

  return segments;
}

// 「https://」だけ、「www.」だけといった不完全な文字列を弾く。
function isPlausibleUrl(url) {
  if (url.length > MAX_URL_LENGTH) return false;
  if (/^www\./i.test(url)) {
    return /^www\.[A-Za-z0-9-]+\.[A-Za-z]{2,}/i.test(url);
  }
  return /^https?:\/\/[A-Za-z0-9-]/i.test(url);
}

/**
 * href 属性に使える絶対 URL に整える。www. 始まりにはスキームを補完する。
 * @param {string} url
 * @returns {string}
 */
export function normalizeHref(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

/**
 * テキスト中のベタ書き URL を先頭から順に返す。
 * @param {string} text
 * @returns {Array<{ index: number, length: number, url: string, href: string }>}
 *   index / length は引数 text 上の位置と長さ（末尾句読点を除いた URL の長さ）。
 */
export function findUrls(text) {
  if (typeof text !== "string" || text === "") return [];

  const matches = [];
  // 正規表現インスタンスを再利用するため、走査前に必ず lastIndex を戻す。
  URL_PATTERN.lastIndex = 0;

  let match;
  while ((match = URL_PATTERN.exec(text)) !== null) {
    const index = match.index;
    const precedingChar = index > 0 ? text[index - 1] : "";
    if (precedingChar && PRECEDING_REJECT.test(precedingChar)) continue;

    // 末尾句読点の除去は断片ごとに行う。区切りで割ったあとの各断片が、
    // それぞれ独立した URL として同じ判定を受ける。
    splitAtEmbeddedScheme(match[0]).forEach(({ offset, value }) => {
      const url = trimTrailingPunctuation(value);
      if (!isPlausibleUrl(url)) return;

      matches.push({
        index: index + offset,
        length: url.length,
        url,
        href: normalizeHref(url)
      });
    });
  }

  return matches;
}
